import React, { useEffect, useState } from "react"
import { Box, Button } from "@mui/material"
import { useTranslation } from "react-i18next"
import { Layout } from "src/components/layout"
import { useAuth } from "src/providers/LoginProvider"
import { Company, CompanyService } from "src/openapi"
import { ShowToast } from "../../states/showToast"
import { ShowProgress } from "../../states/showProgress"
import { useRecoilState } from "recoil"
import { CompanyTable } from "./CompanyTable"
import { SIDE_MENU, SNACKBAR_TYPES, SORT_ORDER } from "src/modules/const"
import BreadCrumb from "src/components/atom/BreadCrumb"
import KeyboardDoubleArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftOutlined"
import KeyboardDoubleArrowRightOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined"
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined"
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined"
import "./index.scss"
import { Top } from "../top"

const PAGE_SIZE = 20

export const CompanyList = () => {
  const { t } = useTranslation("translation", { keyPrefix: "company" })

  const [list, setList] = useState<Company[]>([])

  const { user } = useAuth()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [page, setPage] = useState(0)
  const [total, setTotal] = useState<number>(0)

  const [orderBy] = useState<string>("id")
  const [order] = useState<string>(SORT_ORDER.ASCENDING)

  const [from, setFrom] = useState<number>(0)
  const [to, setTo] = useState<number>(0)
  const [pageCnt, setPageCnt] = useState<number>(0)

  const [reload, setReload] = useState<boolean>(false)

  const setPaginations = (p: number) => {
    if (total > 0) {
      setFrom(p * PAGE_SIZE + 1)

      if (p == pageCnt - 1) {
        setTo(p * PAGE_SIZE + (total % PAGE_SIZE))
      } else {
        setTo(p * PAGE_SIZE + PAGE_SIZE)
      }
    }
  }

  const nextPage = () => {
    if (page < pageCnt - 1) {
      setPage(page + 1)
      setPaginations(page + 1)
    }
  }

  const prevPage = () => {
    if (page > 0) {
      setPage(page - 1)
      setPaginations(page - 1)
    }
  }

  const firstPage = () => {
    if (page != 0) {
      setPage(0)
      setPaginations(0)
    }
  }

  const lastPage = () => {
    if (page != pageCnt - 1) {
      setPage(pageCnt - 1)
    }
  }

  const handleGetCompanyList = () => {
    if (user == null) {
      return
    }

    setShowProgress(true)
    CompanyService.listCompanies(user.token, PAGE_SIZE, page * PAGE_SIZE, orderBy, order)
      .then((res) => {
        setShowProgress(false)
        setList(res.companies as Company[])
        setTotal(res.count!)

        let cnt = Math.ceil(res.count! / PAGE_SIZE)
        setPageCnt(cnt)
        if (res.count! > 0) {
          setFrom(page * PAGE_SIZE + 1)

          if (page == cnt - 1) {
            setTo(page * PAGE_SIZE + (res.count! % PAGE_SIZE))
          } else {
            setTo(page * PAGE_SIZE + PAGE_SIZE)
          }
        } else {
          setFrom(0)
          setTo(0)
          setPageCnt(0)
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({
          show: true,
          text: t("failed_get_company_list"),
          type: SNACKBAR_TYPES.ERROR,
        })
      })
  }

  useEffect(() => {
    if (user == null) {
      return
    }
    handleGetCompanyList()
  }, [user, page])

  useEffect(() => {
    if (user == null) {
      return
    }
    if (!reload) {
      return
    }

    setReload(false)
    handleGetCompanyList()
  }, [user, reload])

  if (!user) {
    return <Top />
  }

  return (
    <Layout menu={SIDE_MENU.COMPANY_LIST}>
      <Box className="flex-container">
        <Box className="header">
          <BreadCrumb
            paths={[
              {
                name: t("list_title"),
                link: "",
              },
            ]}
          />
          <Box className="company-nav-bar">
            <Box className="filter-section"></Box>
            <Box className="pagination-section">
              <Box className="pagination-description">
                {t("pagination", { total: total, from: from, to: to })}
              </Box>
              <Box className="pagination-buttons">
                <Button sx={{ width: "30px!important" }} disabled={page == 0} onClick={firstPage}>
                  <KeyboardDoubleArrowLeftOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
                <Button disabled={page == 0} onClick={prevPage}>
                  <KeyboardArrowLeftOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
                <Button disabled={pageCnt == 0 || page == pageCnt - 1} onClick={nextPage}>
                  <KeyboardArrowRightOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
                <Button
                  sx={{ mr: "0px !important" }}
                  disabled={pageCnt == 0 || page == pageCnt - 1}
                  onClick={lastPage}
                >
                  <KeyboardDoubleArrowRightOutlinedIcon sx={{ fontSize: "20px" }} />
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="content">
          <CompanyTable data={list}></CompanyTable>
        </Box>
      </Box>
    </Layout>
  )
}
